'use client';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { AssetIcon } from '@/blocks/components/AssetIcon';
import { WithTrendColor } from '@/blocks/components/WithTrendColor';
import { FormatAssetName, FormatPercent, FormatPrice } from '@/blocks/formatters';
import { extractAssetName } from '@/blocks/formatters/format-asset-name';
import { LinkI18n } from '@/i18n/routing';
import { EventBlockNames } from '@/providers/analytics/types';
import typography from '@/styles/typography.module.scss';

import { Card, cardStyles } from '@/app/[lang]/price-chart/[category]/[symbol]/components/Card';
import { getAssetsByCategory } from '@/app/[lang]/price-chart/[category]/data';
import { getAssetPageLink } from '@/app/[lang]/price-chart/[category]/navigation-utils';
import { Asset as LocalAsset } from '@/app/[lang]/price-chart/[category]/types';

import styles from './styles.module.scss';

export function SimilarAssetsWidget({
  asset,
  initialData,
}: {
  asset: LocalAsset;
  initialData: RecapAsset[];
}) {
  const tr = useTranslations('pages.priceCharts.asset.sections.sidebar.similarAssets');
  const { markets } = useRecap(undefined, initialData);

  if (!markets) {
    return null;
  }

  const similar = getAssetsByCategory(asset.category)
    .filter((localAsset) => localAsset.name !== asset.name)
    .map((localAsset) => ({
      ...localAsset,
      ...markets.find((marketsAsset) => marketsAsset.name === localAsset.name),
    })) as RecapAsset[];

  if (!similar.length) {
    return null;
  }

  return (
    <Card
      header={tr.rich('title')}
      headerClassname={typography.subheadingM}
      className={cardStyles.cardPaddingRegular}
    >
      <ul className={styles.similarAssetsList} data-event-block-name={EventBlockNames.similar_assets}>
        {similar
          .map((asset) => {
            return (
              <li key={asset.name}>
                <LinkI18n
                  className={styles.link}
                  data-event-label={extractAssetName(asset)}
                  href={getAssetPageLink(asset.name)}
                >
                  <AssetIcon assetName={asset.name} className={styles.icon} />
                  <div className={styles.text}>
                    <span className={classNames(typography.bodyM, styles.title)}>
                      <FormatAssetName assetName={extractAssetName(asset)} />
                    </span>
                    <div className={styles.priceAndChange}>
                      <WithTrendColor
                        negative={asset.change24h < 0}
                        className={classNames(styles.change, typography.bodyS)}
                      >
                        <FormatPercent value={asset.change24h} forcePlusSign={true} />
                      </WithTrendColor>
                      <span className={classNames(styles.price, typography.bodyS)}>
                        <FormatPrice price={asset.price} priceScale={asset.priceScale} />
                      </span>
                    </div>
                  </div>
                </LinkI18n>
              </li>
            );
          })
          .slice(0, 6)}
      </ul>
    </Card>
  );
}
